import { useRef, useState, type KeyboardEvent } from 'react';
import { X } from 'lucide-react';
import { Chip } from './Chip';

/**
 * Free-form multi-tag input. Press Enter or comma to add a tag; click × to
 * remove. `suggestions` narrows as the user types — select with mouse or
 * keyboard (↑↓ Enter).
 */
export function TagInput({
  value,
  onChange,
  suggestions = [],
  placeholder = 'Add a tag…',
}: {
  value: string[];
  onChange: (tags: string[]) => void;
  suggestions?: string[];
  placeholder?: string;
}) {
  const [input, setInput] = useState('');
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const trimmed = input.trim().toLowerCase();

  const candidates = suggestions.filter(
    (s) =>
      s.toLowerCase().includes(trimmed) &&
      !value.includes(s),
  );

  const addTag = (raw: string) => {
    const tag = raw.trim().toLowerCase().replace(/,+$/, '');
    if (!tag || value.includes(tag) || tag.length > 50) return;
    onChange([...value, tag]);
    setInput('');
    setOpen(false);
    setHighlighted(0);
  };

  const removeTag = (tag: string) => {
    onChange(value.filter((t) => t !== tag));
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      if (open && candidates.length > 0) {
        addTag(candidates[highlighted] ?? input);
      } else {
        addTag(input);
      }
    } else if (e.key === 'Backspace' && input === '' && value.length > 0) {
      onChange(value.slice(0, -1));
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlighted((h) => Math.min(h + 1, candidates.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const handleChange = (v: string) => {
    const raw = v.replace(/,/g, '');
    if (v.includes(',')) {
      // User typed a comma — commit what's before it
      addTag(v.split(',')[0]);
      return;
    }
    setInput(raw);
    setOpen(raw.length > 0);
    setHighlighted(0);
  };

  return (
    <div className="relative">
      <div
        className="input flex min-h-[38px] flex-wrap gap-1.5 py-1.5 cursor-text"
        onClick={() => inputRef.current?.focus()}
      >
        {value.map((tag) => (
          <span
            key={tag}
            className="inline-flex items-center gap-1 rounded border border-line bg-surface-2 px-2 py-0.5 text-xs text-ink-muted"
          >
            {tag}
            <button
              type="button"
              className="text-ink-faint hover:text-ink focus:outline-none"
              onClick={(e) => {
                e.stopPropagation();
                removeTag(tag);
              }}
              aria-label={`Remove tag ${tag}`}
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          className="min-w-[120px] flex-1 bg-transparent text-sm text-ink outline-none placeholder:text-ink-faint"
          value={input}
          onChange={(e) => handleChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => setOpen(trimmed.length > 0 && candidates.length > 0)}
          onBlur={() => {
            // Slight delay so click on dropdown item fires first
            setTimeout(() => setOpen(false), 150);
          }}
          placeholder={value.length === 0 ? placeholder : ''}
          aria-label="Tag input"
          autoComplete="off"
        />
      </div>

      {open && candidates.length > 0 && (
        <ul className="absolute z-20 mt-1 w-full rounded-md border border-line bg-surface-1 py-1 shadow-md">
          {candidates.slice(0, 10).map((s, i) => (
            <li key={s}>
              <button
                type="button"
                className={`w-full px-3 py-1.5 text-left text-sm ${
                  i === highlighted
                    ? 'bg-surface-2 text-ink'
                    : 'text-ink-muted hover:bg-surface-2 hover:text-ink'
                }`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  addTag(s);
                }}
                onMouseEnter={() => setHighlighted(i)}
              >
                {s}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Read-only display of tags as chips — used in issue rows and detail page. */
export function TagChips({ tags, className = '' }: { tags: string[]; className?: string }) {
  if (tags.length === 0) return null;
  return (
    <span className={`flex flex-wrap items-center gap-1 ${className}`}>
      {tags.map((tag) => (
        <Chip key={tag}>{tag}</Chip>
      ))}
    </span>
  );
}
